import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

const STATUS_STYLES = {
  pending: "rounded-full bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
  approved: "rounded-full bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800",
  completed: "rounded-full bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800",
  declined: "rounded-full bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800",
  cancelled: "rounded-full bg-zinc-50 text-zinc-500 border-zinc-200 dark:bg-zinc-800/20 dark:text-zinc-400 dark:border-zinc-700",
};

function fmtAmt(n) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n); }
function fmtDate(iso) { if (!iso) return "—"; return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }

export default function AdminFunds() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { requestId, newStatus, name, amount }
  const [acting, setActing] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/funds-requests");
      setRequests(data.requests || []);
    } catch (e) { console.error("Fetch funds error:", e); toast.error("Failed to load fund requests."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const confirmStatusChange = async () => {
    if (!dialog) return;
    setActing(true);
    try {
      await api.post(`/admin/funds-requests/${dialog.requestId}/status`, { status: dialog.newStatus });
      toast.success(`Request marked as ${dialog.newStatus}.`);
      fetchRequests();
    } catch (e) { console.error("Status change error:", e); toast.error("Failed to update status."); }
    finally { setActing(false); setDialog(null); }
  };

  const onStatusSelect = (requestId, newStatus, req) => {
    setDialog({ requestId, newStatus, name: req.name, amount: fmtAmt(req.amount), type: req.type });
  };

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div data-testid="admin-funds" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Funds Requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage all deposit and withdrawal requests across clients.</p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800">
            {pendingCount} pending
          </Badge>
        )}
      </div>

      <Card className="rounded-xl ring-1 ring-black/5 dark:ring-white/5 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">No fund requests yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="funds-table">
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="font-medium">Client</TableHead>
                  <TableHead className="font-medium">Type</TableHead>
                  <TableHead className="font-medium text-right">Amount</TableHead>
                  <TableHead className="font-medium">Status</TableHead>
                  <TableHead className="font-medium">Note</TableHead>
                  <TableHead className="font-medium">Date</TableHead>
                  <TableHead className="font-medium text-right">Update Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.request_id} data-testid={`fund-row-${r.request_id}`} className="hover:bg-muted/30 transition-colors duration-150">
                    <TableCell>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{r.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{r.email || r.account_no}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5 text-sm">
                        {r.type === "deposit" ? <ArrowDownRight className="size-3.5 text-emerald-600" /> : <ArrowUpRight className="size-3.5 text-amber-600" />}
                        {r.type === "deposit" ? "Deposit" : "Withdrawal"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums font-medium">{fmtAmt(r.amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${STATUS_STYLES[r.status] || ""}`}>{(r.status || "").toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="text-xs text-muted-foreground truncate block">{r.note || "—"}</span>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {r.status === "pending" || r.status === "approved" ? (
                        <Select onValueChange={(v) => onStatusSelect(r.request_id, v, r)}>
                          <SelectTrigger data-testid={`status-select-${r.request_id}`} className="h-7 w-[130px] text-xs rounded-lg ml-auto">
                            <SelectValue placeholder="Change…" />
                          </SelectTrigger>
                          <SelectContent>
                            {r.status === "pending" && <SelectItem value="approved">Approve</SelectItem>}
                            {r.status === "pending" && <SelectItem value="declined">Decline</SelectItem>}
                            {(r.status === "pending" || r.status === "approved") && <SelectItem value="completed">Complete</SelectItem>}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Fund Request</AlertDialogTitle>
            <AlertDialogDescription>
              Mark {dialog?.name}'s {dialog?.type} of {dialog?.amount} as <strong>{dialog?.newStatus}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="funds-dialog-cancel" disabled={acting}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="funds-dialog-confirm" onClick={confirmStatusChange} disabled={acting} className="bg-brand text-white hover:bg-[#0B8E8D]">
              {acting ? "Updating…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
